import { useEffect } from 'react'

interface DocumentHeadOptions {
  title?: string
  description?: string
  keywords?: string[]
  ogImage?: string
  ogType?: 'website' | 'article'
  noIndex?: boolean
}

const APP_NAME = 'Templatean Admin'
const DEFAULT_DESCRIPTION = 'AI-powered Office add-ins platform for document management and template generation'

function setMetaTag(name: string, content: string, property = false) {
  const attr = property ? 'property' : 'name'
  let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null

  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attr, name)
    document.head.appendChild(meta)
  }

  meta.setAttribute('content', content)
}

function removeMetaTag(name: string, property = false) {
  const attr = property ? 'property' : 'name'
  const meta = document.querySelector(`meta[${attr}="${name}"]`)
  if (meta) {
    meta.remove()
  }
}

export function useDocumentHead({
  title,
  description = DEFAULT_DESCRIPTION,
  keywords = [],
  ogImage,
  ogType = 'website',
  noIndex = false,
}: DocumentHeadOptions) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${APP_NAME}` : APP_NAME
    const prevTitle = document.title

    // Set title
    document.title = fullTitle

    // Set description
    setMetaTag('description', description)

    // Set keywords
    if (keywords.length > 0) {
      setMetaTag('keywords', keywords.join(', '))
    } else {
      removeMetaTag('keywords')
    }

    // Open Graph
    setMetaTag('og:title', fullTitle, true)
    setMetaTag('og:description', description, true)
    setMetaTag('og:type', ogType, true)

    if (ogImage) {
      setMetaTag('og:image', ogImage, true)
    } else {
      removeMetaTag('og:image', true)
    }

    // Twitter
    setMetaTag('twitter:card', 'summary_large_image')
    setMetaTag('twitter:title', fullTitle)
    setMetaTag('twitter:description', description)

    if (ogImage) {
      setMetaTag('twitter:image', ogImage)
    } else {
      removeMetaTag('twitter:image')
    }

    // Robots
    if (noIndex) {
      setMetaTag('robots', 'noindex, nofollow')
    } else {
      removeMetaTag('robots')
    }

    // Cleanup on unmount
    return () => {
      document.title = prevTitle
    }
  }, [title, description, keywords, ogImage, ogType, noIndex])
}
